import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AVAILABLE_FEATURES, useReadinessModule, type FeatureChip, type FeatureDef } from '@/lib/readiness/module'
import { SyncStatus } from '@/components/readiness/shell'
import { Icon } from '@/components/readiness/primitives'
import { useReadinessStore } from '@/stores/readinessStore'
import { useQueryClient } from '@tanstack/react-query'
import { useMigrationStore } from '@/stores/migrationStore'
import { fetchMigrationFacts } from '@/lib/migration/facts'
import { migrationApi } from '@/api/clients/migrationApi'
import { planKeys } from '@/lib/migration/usePlan'
import { useActiveFeature } from '@/lib/readiness/nav'
import { SyncSummaryModal, ServedSyncSummaryModal, type ServedSyncSummary } from '@/components/readiness/SyncSummary'
import { assessmentsApi, isEditable } from '@/api/clients/assessmentsApi'
import { LockedItem } from '@/components/readiness/LockedItem'
import { isLocked, useProfileStore } from '@/stores/profileStore'
import { useSyncActionStore } from '@/stores/syncActionStore'

// ── Module navigation (Iteration 3 §1, Iteration 5 §1) ──────────────────────
// The module tab strip under the app header on every Readiness route. It
// renders the available features of READINESS_FEATURES with chips and counts
// from `useReadinessModule()`; the product sidebar lists the same features as
// the Readiness group (components/layout/navigation.ts), so they cannot
// differ. The active entry follows the page's origin on detail routes (I5 §4).

const MAX_TABS = 6 // at seven or more, the lowest-traffic tabs collapse into More ▾

function ChipView({ c }: { c: FeatureChip }) {
  if (c.chip) {
    const tone = c.chip.tone === 'ok' ? { background: 'var(--rd-status-ok-bg)', color: 'var(--rd-status-ok-text)' } : c.chip.tone === 'warn' ? { background: 'var(--rd-status-warn-bg)', color: 'var(--rd-status-warn)' } : { background: 'var(--rd-accent-chip-bg)', color: 'var(--rd-accent-chip-fg)' }
    return <span className="rd-chip rd-chip-sm" style={{ ...tone, letterSpacing: '0.06em' }}>{c.chip.text}</span>
  }
  if (c.count != null) return <span className="rd-mono text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{c.count}</span>
  return null
}

const hrefFor = (f: FeatureDef, indexId: string) => `${f.route}?index=${indexId}`

export function ModuleStrip() {
  const ctx = useReadinessModule()
  const activeKey = useActiveFeature()
  const track = useReadinessStore((s) => s.track)
  const migrationSync = useMigrationStore((s) => s.sync)
  const qc = useQueryClient()
  const gates = useProfileStore((s) => s)
  const [more, setMore] = useState(false)
  // Sync summary (I5 §16): opens after a manual sync with changes, or from the
  // strip's `{n} changes` link after an automatic one.
  const [summaryRun, setSummaryRun] = useState<string | null>(null)
  const [served, setServed] = useState<ServedSyncSummary | null>(null)
  const navigate = useNavigate()
  // The open QRI assessment a sync suggests levels on, if the service has one.
  const openAssessmentId = async () => {
    if (ctx.indexId !== 'csa-qri-v1' || !ctx.orgId) return null
    const all = await assessmentsApi.list(ctx.orgId).catch(() => [])
    return all.find((a) => isEditable(a.status))?.id ?? null
  }
  const shown = AVAILABLE_FEATURES.length > MAX_TABS ? AVAILABLE_FEATURES.slice(0, MAX_TABS - 1) : AVAILABLE_FEATURES
  const overflow = AVAILABLE_FEATURES.length > MAX_TABS ? AVAILABLE_FEATURES.slice(MAX_TABS - 1) : []
  const syncNow = async () => {
    // A minute's slack covers a browser clock ahead of the service's.
    const startedAt = new Date()
    const since = new Date(startedAt.getTime() - 60_000).toISOString()
    await ctx.sync('manual')
    // The plan and tracker sources follow the readiness sync (I3 §12 of I2).
    try {
      const facts = await fetchMigrationFacts(ctx.orgId, useReadinessStore.getState().scopes[`${ctx.orgId || 'anon'}::${ctx.indexId}`]?.settings.discovery_facts ?? null)
      migrationSync(ctx.orgId, ctx.indexId, facts, ctx.actor)
      await migrationApi.sync(ctx.orgId, ctx.indexId, { actor: ctx.actor })
      await qc.invalidateQueries({ queryKey: planKeys.root(ctx.orgId, ctx.indexId) })
    } catch {
      /* the readiness sync already reported its own state */
    }
    // A served assessment's summary is what the service recorded since the sync started.
    const assessmentId = await openAssessmentId()
    if (assessmentId) {
      const entries = await assessmentsApi.activity(ctx.orgId, assessmentId, since).catch(() => [])
      const runs = (qc.getQueryData(['readiness-sync', ctx.orgId]) as { runs?: Array<{ id: string; status: string; started_at: string }> } | undefined)?.runs ?? []
      const failed = ['discovery', 'playbook'].filter((m) => runs.find((r) => r.id.startsWith(`${m}-`) && r.started_at >= since)?.status === 'failed').map((m) => (m === 'discovery' ? 'Discovery' : 'Playbook'))
      setServed({ startedAt: startedAt.toISOString(), entries, failed })
      return
    }
    const run = useReadinessStore.getState().scopes[`${ctx.orgId || 'anon'}::${ctx.indexId}`]?.sync_runs[0]
    if (run && run.status === 'succeeded' && (run.changes?.length ?? 0) > 0) setSummaryRun(run.id)
  }
  // `Sync now` lives in the user menu, not the strip: publish the action, kept
  // stable through a ref so the menu does not re-render on every strip render.
  const setAction = useSyncActionStore((s) => s.setAction)
  const setSyncing = useSyncActionStore((s) => s.setSyncing)
  const syncRef = useRef(syncNow)
  useEffect(() => {
    syncRef.current = syncNow
  })
  useEffect(() => {
    setAction(() => syncRef.current())
    return () => setAction(null)
  }, [setAction])
  useEffect(() => setSyncing(ctx.syncing), [setSyncing, ctx.syncing])
  const tab = (f: FeatureDef) => {
    const active = f.key === activeKey
    const className = 'flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-[8px] px-[14px] text-[14px] font-semibold'
    const style = active ? { background: 'var(--rd-tab-active)', color: 'var(--rd-text-primary)', boxShadow: 'inset 0 0 0 1px var(--rd-tab-ring)' } : { color: 'var(--rd-text-muted)' }
    // POC (I6 POC §4): locked features keep their tab and count, with a leading
    // lock; the tab still navigates to the locked preview.
    if (f.gate && isLocked(gates, f.gate)) {
      return (
        <LockedItem
          key={f.key}
          feature={f.gate}
          variant="tab"
          to={hrefFor(f, ctx.indexId)}
          onNavigate={() => track(ctx.orgId, ctx.indexId, { name: 'readiness.module_tab_opened', tab: f.key })}
          className={className}
          style={style}
          chip={<ChipView c={ctx.chips[f.key]} />}
          testId={`module-tab-${f.key}`}
          linkProps={{ role: 'tab', 'aria-selected': active, 'aria-current': active ? 'page' : undefined }}
        >
          {f.label}
        </LockedItem>
      )
    }
    return (
      <Link
        key={f.key}
        to={hrefFor(f, ctx.indexId)}
        role="tab"
        aria-selected={active}
        aria-current={active ? 'page' : undefined}
        onClick={() => track(ctx.orgId, ctx.indexId, { name: 'readiness.module_tab_opened', tab: f.key })}
        className={className}
        style={style}
        data-testid={`module-tab-${f.key}`}
      >
        {f.label}
        <ChipView c={ctx.chips[f.key]} />
      </Link>
    )
  }
  return (
    <div className="rd rd-no-print sticky top-[57px] z-20 flex h-[52px] items-center gap-3 px-4 sm:px-6" style={{ background: 'var(--rd-bg-header)', borderBottom: '1px solid var(--rd-border-subtle)' }} data-testid="module-strip">
      <nav aria-label="Readiness" className="min-w-0 flex-1 overflow-x-auto">
        <div role="tablist" className="flex items-center gap-1">
          {shown.map(tab)}
          {overflow.length > 0 && (
            <span className="relative">
              <button type="button" className="flex h-9 items-center gap-1 rounded-[8px] px-3 text-[14px] font-semibold" style={{ color: 'var(--rd-text-muted)' }} onClick={() => setMore((v) => !v)} aria-expanded={more}>More <Icon name="chevron" size={14} /></button>
              {more && <div className="rd-card absolute left-0 top-full z-30 mt-1 w-56 p-1" style={{ boxShadow: 'var(--rd-shadow-float)' }}>{overflow.map(tab)}</div>}
            </span>
          )}
        </div>
      </nav>
      <div className="hidden shrink-0 items-center gap-3 text-[13px] md:flex [&>span]:!text-[13px]">
        <SyncStatus ctx={ctx} onOpenSummary={(id) => setSummaryRun(id)} />
      </div>
      {summaryRun && <SyncSummaryModal ctx={ctx} runId={summaryRun} onClose={() => setSummaryRun(null)} />}
      {served && (
        <ServedSyncSummaryModal
          ctx={ctx}
          summary={served}
          onClose={() => setServed(null)}
          // The dashboard opens the objective's drawer, where it is reviewed.
          onOpen={(code) => {
            setServed(null)
            navigate(`/readiness?index=${ctx.indexId}`, { state: { openObjective: code } })
          }}
        />
      )}
    </div>
  )
}
